import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronUp, Star } from "lucide-react";
import { useLocation } from "react-router-dom";

const MESSAGES = {
  tr: [
    { text: "🚀 ROAS değerimiz 0.8'den 4.7'ye çıktı.", author: "Bigist Invest Müşterisi" },
    { text: "📈 Sadece 90 günde sosyal medya takipçilerimiz 3 katına çıktı.", author: "Amorium Hotel" },
    { text: "⚡ Kurumsal web sitemiz 2 hafta içinde beklediğimizden çok daha hızlı yayına alındı.", author: "DIO Medikal" },
    { text: "✅ Reklamlarım artık doğru sonuç getiriyor, çok teşekkür ederim!", author: "HB Dijital Müşterisi" },
    { text: "🎯 SEO çalışmalarıyla 6 ayda Google'da ilk sayfaya çıktık.", author: "Uğur Okulları" },
  ],
  en: [
    { text: "🚀 Our ROAS went from 0.8 to 4.7.", author: "Bigist Invest Client" },
    { text: "📈 In just 90 days, our social media followers tripled.", author: "Amorium Hotel" },
    { text: "⚡ Our corporate website went live much faster than we expected, in 2 weeks.", author: "DIO Medikal" },
    { text: "✅ My ads are now delivering real results. Thank you so much!", author: "HB Digital Client" },
    { text: "🎯 With SEO work, we reached Google's first page in 6 months.", author: "Ugur Schools" },
  ],
  es: [
    { text: "🚀 Nuestro ROAS pasó de 0.8 a 4.7.", author: "Cliente Bigist Invest" },
    { text: "📈 En solo 90 días, nuestros seguidores se triplicaron.", author: "Amorium Hotel" },
    { text: "⚡ Nuestro sitio web corporativo se lanzó mucho más rápido de lo esperado.", author: "DIO Medikal" },
    { text: "✅ Mis anuncios ahora generan resultados reales. ¡Muchas gracias!", author: "Cliente HB Digital" },
    { text: "🎯 Con trabajo SEO, llegamos a la primera página de Google en 6 meses.", author: "Uğur Okulları" },
  ],
  de: [
    { text: "🚀 Unser ROAS stieg von 0,8 auf 4,7.", author: "Bigist Invest Kunde" },
    { text: "📈 In nur 90 Tagen haben sich unsere Social-Media-Follower verdreifacht.", author: "Amorium Hotel" },
    { text: "⚡ Unsere Website ging viel schneller live als erwartet — in 2 Wochen.", author: "DIO Medikal" },
    { text: "✅ Meine Anzeigen liefern jetzt echte Ergebnisse. Vielen Dank!", author: "HB Digital Kunde" },
    { text: "🎯 Mit SEO-Arbeit erreichten wir in 6 Monaten die erste Google-Seite.", author: "Uğur Okulları" },
  ],
  fr: [
    { text: "🚀 Notre ROAS est passé de 0,8 à 4,7.", author: "Client Bigist Invest" },
    { text: "📈 En seulement 90 jours, nos abonnés ont triplé.", author: "Amorium Hotel" },
    { text: "⚡ Notre site web a été mis en ligne beaucoup plus vite que prévu.", author: "DIO Medikal" },
    { text: "✅ Mes publicités donnent maintenant de vrais résultats. Merci beaucoup !", author: "Client HB Digital" },
    { text: "🎯 Grâce au SEO, nous avons atteint la première page Google en 6 mois.", author: "Uğur Okulları" },
  ],
  ar: [
    { text: "🚀 ارتفع عائد الإنفاق الإعلاني من 0.8 إلى 4.7.", author: "عميل Bigist Invest" },
    { text: "📈 في 90 يوماً فقط، تضاعف متابعونا ثلاث مرات.", author: "Amorium Hotel" },
    { text: "⚡ تم إطلاق موقعنا أسرع بكثير مما توقعنا.", author: "DIO Medikal" },
    { text: "✅ إعلاناتي تحقق الآن نتائج حقيقية. شكراً جزيلاً!", author: "عميل HB Digital" },
    { text: "🎯 بفضل عمل SEO، وصلنا إلى الصفحة الأولى في Google خلال 6 أشهر.", author: "Uğur Okulları" },
  ],
};

// Fallback for other languages
const FALLBACK_LANG = "en";

const STORAGE_KEY = "ym_banner_dismissed_v2";

export default function AnnouncementBar() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [msgIdx, setMsgIdx] = useState(0);

  // Detect language from URL
  const seg = pathname.split("/").filter(Boolean)[0];
  const lang = ["tr","en","es","de","fr","ar"].includes(seg) ? seg : "tr";
  const messages = MESSAGES[lang] || MESSAGES[FALLBACK_LANG];

  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Cycle messages every 5 seconds
  useEffect(() => {
    if (!visible || minimized) return;
    const interval = setInterval(() => {
      setMsgIdx((i) => (i + 1) % messages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [visible, minimized, messages.length]);

  const dismiss = useCallback(() => {
    setVisible(false);
    sessionStorage.setItem(STORAGE_KEY, "1");
  }, []);

  const toggle = () => setMinimized((v) => !v);

  if (pathname.startsWith("/admin")) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: minimized ? 32 : "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-[60] overflow-hidden"
          style={{ background: "linear-gradient(135deg, #EE4F7A 0%, #8B3D9E 50%, #55D9C2 100%)" }}
          data-testid="announcement-bar"
        >
          {minimized ? (
            /* Minimized strip */
            <div
              className="flex items-center justify-center gap-2 h-8 cursor-pointer"
              onClick={toggle}
            >
              <Star className="h-3 w-3 text-cream/80" />
              <span className="text-cream/90 text-xs font-semibold">
                {lang === "tr" ? "Müşteri başarı hikâyeleri" : "Client success stories"}
              </span>
              <ChevronUp className="h-3 w-3 text-cream/80 rotate-180" />
            </div>
          ) : (
            /* Full bar */
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 min-h-[44px]">
              {/* Left spacer */}
              <div className="w-16 hidden sm:block" />

              {/* Message center */}
              <div className="flex-1 flex items-center justify-center gap-3 min-w-0 text-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={msgIdx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2"
                  >
                    <span className="text-cream text-sm font-medium leading-snug">
                      {messages[msgIdx].text}
                    </span>
                    <span className="text-cream/60 text-xs hidden sm:inline">
                      — {messages[msgIdx].author}
                    </span>
                  </motion.div>
                </AnimatePresence>

                {/* Dot indicators */}
                <div className="hidden sm:flex items-center gap-1 ml-3">
                  {messages.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setMsgIdx(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === msgIdx ? "w-4 bg-cream" : "w-1.5 bg-cream/40"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Right controls */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={toggle}
                  className="h-7 w-7 rounded-full bg-cream/15 hover:bg-cream/25 flex items-center justify-center transition-colors"
                  aria-label="Küçült"
                >
                  <ChevronUp className="h-3.5 w-3.5 text-cream" />
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="h-7 w-7 rounded-full bg-cream/15 hover:bg-cream/25 flex items-center justify-center transition-colors"
                  aria-label="Kapat"
                >
                  <X className="h-3.5 w-3.5 text-cream" />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
